import { useEffect, useCallback, useState, useRef } from "react";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/frontend/components/ui/command";
import {
  DialogTitle,
  DialogDescription,
} from "@/frontend/components/ui/dialog";
import { Button } from "@/frontend/components/ui/button";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useSearchStore } from "../stores/searchStore";
import { useChatStore } from "../stores/chatStore";
import { useUIStore } from "../stores/uiStore";
import { useNavigate } from "react-router";
import { useOrgStore } from "../stores/orgStore";
import { Sparkles, Copy, Check, SlidersHorizontal } from "lucide-react";
import { MessageWithReferences } from "./MessageWithReferences";
import { PendingActionCard } from "./PendingActionCard";
import { convertReferencesToUrls, generateSourceUrl } from "../lib/chat-utils";
import ChatFilterPanel from "./ChatFilterPanel";
import type { ChatFilters } from "../stores/chatStore";
import "./SearchModal.css";

type Mode = "search" | "chat";

export function SearchChatModal() {
  const [mode, setMode] = useState<Mode>("search");
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Search store
  const {
    open: searchOpen,
    setOpen: setSearchOpen,
    results,
    loading: searchLoading,
    error: searchError,
    performSearch,
    clearResults,
    setQuery: setSearchQuery,
  } = useSearchStore();

  // Chat store
  const {
    open: chatOpen,
    setOpen: setChatOpen,
    currentConversation,
    messages,
    sendMessage,
    createConversation,
    clearCurrentConversation,
    streaming,
    loading: chatLoading,
    error: chatError,
    advancedMode,
    setAdvancedMode,
    filters,
    setFilters,
    clearFilters,
  } = useChatStore();

  // Calculate expanded state
  const isExpanded = mode === "chat" && messages.length > 0;

  const navigate = useNavigate();
  const { currentOrgId } = useOrgStore();

  // Combined open state
  const open = searchOpen || chatOpen;
  const setOpen = (value: boolean) => {
    setSearchOpen(value);
    setChatOpen(value);
    if (!value) {
      setMode("search");
      setInput("");
    }
  };

  // Debounced search (only in search mode)
  useEffect(() => {
    if (mode !== "search") return;

    if (!input || input.trim().length < 3) {
      clearResults();
      return;
    }

    if (!currentOrgId) {
      return;
    }

    const timer = setTimeout(() => {
      setSearchQuery(input);
      performSearch(currentOrgId);
    }, 300);

    return () => clearTimeout(timer);
  }, [input, currentOrgId, mode, performSearch, clearResults, setSearchQuery]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      // Cmd/Ctrl+K to toggle modal
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open, setOpen]);

  // Clear on close
  useEffect(() => {
    if (!open) {
      clearResults();
      clearCurrentConversation();
      setMode("search");
      setInput("");
    }
  }, [open, clearResults, clearCurrentConversation]);

  // Auto-scroll to bottom when messages change or during streaming (chat mode)
  useEffect(() => {
    if (mode === "chat" && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, mode]);

  // Continuous scroll during streaming
  useEffect(() => {
    if (mode === "chat" && streaming) {
      const scrollInterval = setInterval(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100); // Scroll every 100ms during streaming

      return () => clearInterval(scrollInterval);
    }
  }, [mode, streaming]);

  const handleSwitchToChat = async () => {
    if (!currentOrgId) return;

    setMode("chat");

    // Create a new conversation if one doesn't exist
    if (!currentConversation) {
      await createConversation(
        currentOrgId,
        input ? `Chat: ${input.substring(0, 50)}` : "New Chat"
      );
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim() || !currentOrgId || streaming) return;

    const messageToSend = input;
    setInput(""); // Clear input immediately

    // Close advanced mode panel to show conversation
    if (advancedMode) {
      setAdvancedMode(false);
    }

    // Switch to chat mode if not already
    if (mode === "search") {
      setMode("chat");
    }

    // Ensure we have a conversation
    let conversationId = currentConversation?.id;
    if (!conversationId) {
      const newConv = await createConversation(
        currentOrgId,
        messageToSend.substring(0, 50)
      );
      conversationId = newConv?.id;

      if (!conversationId) {
        console.error("Failed to create conversation");
        return;
      }
    }

    await sendMessage(currentOrgId, conversationId, messageToSend, true);

    // Scroll to bottom after sending message
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Shift+Enter to navigate to chat page with query
    if (e.key === "Enter" && e.shiftKey) {
      e.preventDefault();
      if (!input.trim() || !currentOrgId) return;

      // Close modal and navigate to chat page
      setSearchOpen(false);
      navigate(`/org/${currentOrgId}/app/chat?q=${encodeURIComponent(input.trim())}`);
      return;
    }

    // Enter to send message (in chat mode) or select result (in search mode)
    if (e.key === "Enter" && !e.shiftKey && mode === "chat") {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSelectSummary = useCallback(
    (result: any) => {
      if (!currentOrgId) return;

      const date = result.metadata.summary_date?.split("T")[0];
      const conversationId = result.metadata.conversation_id;

      if (date && conversationId) {
        navigate(
          `/org/${currentOrgId}/app/inbox/${encodeURIComponent(conversationId)}?date=${date}`
        );
        setOpen(false);
      }
    },
    [currentOrgId, navigate, setOpen]
  );

  const handleSelectMessage = useCallback(
    (result: any) => {
      if (!currentOrgId) return;

      const url = generateSourceUrl(result, currentOrgId);

      if (url) {
        navigate(url);
        setOpen(false);
      }
    },
    [currentOrgId, navigate, setOpen]
  );

  // Group results by source type
  const summaryResults = results.filter((r) => r.sourceType === "summary");
  const messageResults = results.filter((r) => r.sourceType === "message");

  const loading = searchLoading || chatLoading;
  const error = searchError || chatError;

  // Dynamic placeholder based on mode and message count
  const hasMessages = messages.length > 0;
  const placeholderText =
    mode === "search"
      ? "Search conversations and messages..."
      : hasMessages
        ? "Ask a follow up..."
        : "Ask Querygen a question...";

  // Add data attributes to body for CSS targeting
  useEffect(() => {
    if (open) {
      document.body.setAttribute('data-search-chat-modal', 'true');
    } else {
      document.body.removeAttribute('data-search-chat-modal');
    }
  }, [open]);

  useEffect(() => {
    if (isExpanded) {
      document.body.setAttribute('data-chat-expanded', 'true');
    } else {
      document.body.removeAttribute('data-chat-expanded');
    }
  }, [isExpanded]);

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
    >
      <div className="flex h-full">
        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          <VisuallyHidden>
            <DialogTitle>
              {mode === "search"
                ? "Search conversations and messages"
                : "Chat with your data"}
            </DialogTitle>
            <DialogDescription>
              {mode === "search"
                ? "Search through your conversation summaries and messages"
                : "Ask questions and get answers from your messages and summaries"}
            </DialogDescription>
          </VisuallyHidden>

          {/* Input */}
          <div className="relative">
            <CommandInput
              placeholder={placeholderText}
              onValueChange={setInput}
              value={input}
              onKeyDown={handleKeyDown}
              className="flex-1"
            />
            {mode === "chat" && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                <Button
                  variant={advancedMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => setAdvancedMode(!advancedMode)}
                  className="h-8 relative"
                >
                  <SlidersHorizontal className="w-4 h-4 mr-1.5" />
                  {advancedMode ? "Hide Filters" : "Filters"}
                  {(() => {
                    const filterCount =
                      (filters.dateRange ? 1 : 0) +
                      (filters.conversationIds?.length || 0) +
                      (filters.senderNames?.length || 0);
                    return filterCount > 0 && !advancedMode ? (
                      <span className="absolute -top-1 -right-1 bg-green-600 text-white text-[10px] font-semibold rounded-full w-4 h-4 flex items-center justify-center">
                        {filterCount}
                      </span>
                    ) : null;
                  })()}
                </Button>
              </div>
            )}
          </div>

        {loading && mode === "search" && (
          <div className="search-loading">Searching...</div>
        )}

        <CommandList
          style={{
            height: isExpanded ? '550px' : '450px',
            transition: 'height 0.3s ease'
          }}
        >
        {/* Search Mode */}
        {mode === "search" && (
          <>
            {error && (
              <div className="search-error">
                <p>{error}</p>
              </div>
            )}

            {/* Initial empty state - no input yet */}
            {!loading && !error && input.length < 3 && (
              <div className="search-empty-initial">
                <div className="search-empty-icon">
                  <svg className="w-12 h-12 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="search-empty-title">Search your messages</h3>
                <p className="search-empty-description">
                  Find summaries and messages across all your conversations
                </p>
                <div className="search-examples">
                  <p className="search-examples-title">Try searching for:</p>
                  <ul className="search-examples-list">
                    <li>"project updates"</li>
                    <li>"meeting notes"</li>
                    <li>"budget discussion"</li>
                  </ul>
                </div>
                <div className="search-ai-tip">
                  <Sparkles className="w-4 h-4 text-primary-600" />
                  <span>Or press <kbd>⇧ Enter</kbd> to ask Que a question</span>
                </div>
              </div>
            )}

            {/* No results found */}
            {!loading && !error && input.length >= 3 && results.length === 0 && (
              <CommandEmpty className="search-empty">
                <p className="mb-4">
                  No results found for "{input}"
                </p>
                <p className="flex items-center justify-center gap-2">
                  <span>Press</span>
                  <kbd className="px-2 py-1 bg-white dark:bg-card border rounded text-xs font-mono">
                    ⇧ Enter
                  </kbd>
                  <span>to ask Que</span>
                </p>
              </CommandEmpty>
            )}

            {!loading && !error && results.length > 0 && (
              <>
                {summaryResults.length > 0 && (
                  <CommandGroup heading="Summaries" className="search-group">
                    {summaryResults.map((result) => (
                      <CommandItem
                        key={`summary-${result.id}`}
                        onSelect={() => handleSelectSummary(result)}
                        className="search-item"
                      >
                        <div className="search-item-content">
                          <div className="search-item-text">
                            {result.content.length > 120
                              ? `${result.content.substring(0, 120)}...`
                              : result.content}
                          </div>
                          <div className="search-item-meta">
                            <span className="search-badge search-badge-summary">
                              Summary
                            </span>
                            {result.metadata.chat_name && (
                              <span className="search-item-conversation">
                                {result.metadata.chat_name}
                              </span>
                            )}
                            {result.metadata.summary_date && (
                              <>
                                <span className="search-meta-separator">•</span>
                                <span className="search-meta-item">
                                  {new Date(
                                    result.metadata.summary_date
                                  ).toLocaleDateString()}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {messageResults.length > 0 && (
                  <CommandGroup heading="Messages" className="search-group">
                    {messageResults.map((result) => (
                      <CommandItem
                        key={`message-${result.id}`}
                        onSelect={() => handleSelectMessage(result)}
                        className="search-item"
                      >
                        <div className="search-item-content">
                          <div className="search-item-text">
                            {result.content.length > 100
                              ? `${result.content.substring(0, 100)}...`
                              : result.content}
                          </div>
                          <div className="search-item-meta">
                            <span className="search-badge search-badge-message">
                              Message
                            </span>
                            {result.metadata.chat_name && (
                              <span className="search-item-conversation">
                                {result.metadata.chat_name}
                              </span>
                            )}
                            {result.metadata.sender_name && (
                              <>
                                <span className="search-meta-separator">•</span>
                                <span className="search-meta-item">
                                  {result.metadata.sender_name}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </>
            )}
          </>
        )}

        {/* Chat Mode */}
        {mode === "chat" && (
          <div className="chat-container px-4 py-3 space-y-4">
            {messages.length === 0 && !streaming && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Sparkles className="w-10 h-10 text-primary-600 mb-3" />
                <p className="chat-empty-text">
                  Ask a question about your messages and summaries
                </p>
              </div>
            )}

            {messages.map((message, index) => {
              const isUser = message.role === "user";
              const isAssistant = message.role === "assistant";

              if (!isUser && !isAssistant) return null;

              // Show typing indicator for empty assistant messages during streaming
              const isStreaming = isAssistant && streaming && !message.content;

              return (
                <ChatMessage
                  key={message.id || index}
                  message={message}
                  isUser={isUser}
                  isAssistant={isAssistant}
                  isStreaming={isStreaming}
                />
              );
            })}

            {error && (
              <div className="search-error">
                <p>{error}</p>
              </div>
            )}

            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </div>
        )}
      </CommandList>

      {/* Footer with shortcuts */}
      <div className="search-footer">
            {mode === "search" ? (
              <>
                <span className="search-hint">
                  <kbd>↑↓</kbd>
                  navigate
                </span>
                <span className="search-hint">
                  <kbd>Enter</kbd>
                  select
                </span>
                <span className="search-hint">
                  <kbd>⇧</kbd>
                  <kbd>Enter</kbd>
                  ask Que
                </span>
              </>
            ) : (
              <span className="search-hint">
                <kbd>Enter</kbd>
                send message
              </span>
            )}
          </div>
        </div>

        {/* Filter Panel - Only show in chat mode with advanced mode enabled */}
        {mode === "chat" && advancedMode && (
          <div className="w-96 border-l h-full">
            <ChatFilterPanel
              filters={filters}
              onChange={setFilters}
              onClear={clearFilters}
            />
          </div>
        )}
      </div>
    </CommandDialog>
  );
}

/**
 * Individual chat message component with copy functionality
 */
function ChatMessage({
  message,
  isUser,
  isAssistant,
  isStreaming = false,
}: {
  message: any;
  isUser: boolean;
  isAssistant: boolean;
  isStreaming?: boolean;
}) {
  const { copiedMessages, setCopied } = useUIStore();
  const { currentOrgId } = useOrgStore();
  const isCopied = copiedMessages[message.id] || false;

  const handleCopy = async () => {
    try {
      // Convert references to URLs if there are sources
      const textToCopy = isAssistant && message.metadata?.sources && currentOrgId
        ? convertReferencesToUrls(message.content, message.metadata.sources, currentOrgId)
        : message.content;

      await navigator.clipboard.writeText(textToCopy);
      setCopied(message.id);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  return (
    <div className="chat-message-wrapper">
      {/* Header with role label and copy button */}
      <div className="flex items-center justify-between mb-2">
        <div className={`chat-role-label ${isUser ? "chat-role-user" : "chat-role-assistant"}`}>
          {isUser ? "You" : "Querygen"}
        </div>
        {isAssistant && !isStreaming && (
          <button
            onClick={handleCopy}
            className="chat-copy-button"
            title="Copy message"
          >
            {isCopied ? (
              <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
          </button>
        )}
      </div>

      {/* Message Content */}
      <div className="chat-message-content">
        {isStreaming ? (
          <span className="chat-typing-indicator">
            <span className="chat-typing-dot"></span>
            <span className="chat-typing-dot"></span>
            <span className="chat-typing-dot"></span>
          </span>
        ) : isAssistant ? (
          <>
            <MessageWithReferences
              content={message.content}
              sources={message.metadata?.sources}
              className="chat-markdown"
            />
            <PendingActionCard message={message} />
          </>
        ) : (
          message.content
        )}
      </div>

      {/* Search indicator for assistant */}
      {isAssistant && message.metadata?.toolCalls && (
        <div className="chat-search-indicator">
          <Sparkles className="w-3.5 h-3.5" />
          <span>Used search</span>
        </div>
      )}
    </div>
  );
}
